import { claveDeVariante, combinaciones } from '@mc/comercio'
import { claveDesdeEtiqueta } from '@mc/formularios'

import { idDeFila } from '../rutas'

/**
 * Lo que necesitan los controles propios (`Especiales.tsx`) para no pedir texto a mano:
 * qué campos de un formulario se pueden nombrar, qué respuestas tiene cada uno, qué
 * variantes faltan de un producto. Funciones puras, con su prueba; las cuentas de las
 * variantes y de las claves son las del núcleo (`@mc/comercio`, `@mc/formularios`).
 */
type Fila = Record<string, unknown>

const texto = (v: unknown, alternativa = ''): string => (typeof v === 'string' ? v : alternativa)

export type CampoNombrable = { clave: string; etiqueta: string; tipo: string; opciones: Array<{ etiqueta: string; valor: string }> }

/** Los campos de un formulario (tal como están en el editor, con `blockType`) que tienen respuesta. */
export const camposNombrables = (lista: unknown, tipos?: unknown): CampoNombrable[] => {
  const filtro = Array.isArray(tipos) ? (tipos as string[]) : null
  return (Array.isArray(lista) ? (lista as Fila[]) : [])
    .filter((c) => texto(c.clave) && c.blockType !== 'paso' && c.blockType !== 'informativo' && (!filtro || filtro.includes(texto(c.blockType))))
    .map((c) => ({
      clave: texto(c.clave),
      etiqueta: texto(c.etiqueta) || texto(c.clave),
      tipo: texto(c.blockType),
      opciones: (Array.isArray(c.opciones) ? (c.opciones as Fila[]) : []).map((o) => ({ etiqueta: texto(o.etiqueta) || texto(o.valor), valor: texto(o.valor) })).filter((o) => o.valor),
    }))
}

/** Las respuestas posibles de un campo, para elegir una en vez de escribirla: sus opciones, o «aceptado» en un consentimiento. */
export const respuestasDe = (campo: CampoNombrable | undefined): Array<{ etiqueta: string; valor: string }> => {
  if (!campo) return []
  if (campo.opciones.length > 0) return campo.opciones
  if (campo.tipo === 'consentimiento') return [{ etiqueta: 'Aceptado', valor: 'true' }]
  return []
}

/** La clave que se propone para una fila nueva, sin repetir las de sus hermanas. */
export const clavePropuesta = (etiqueta: string, ocupadas: string[]): string => claveDesdeEtiqueta(etiqueta, ocupadas)

export type OpcionDeProducto = { nombre: string; valores: string[] }

export const opcionesDelProducto = (lista: unknown): OpcionDeProducto[] =>
  (Array.isArray(lista) ? (lista as Fila[]) : [])
    .map((o) => ({ nombre: texto(o.nombre).trim(), valores: (Array.isArray(o.valores) ? (o.valores as Fila[]) : []).map((v) => texto(v.valor).trim()).filter(Boolean) }))
    .filter((o) => o.nombre && o.valores.length > 0)

/** La clave de una variante tal como está en el editor: la misma que calcula el núcleo al guardar. */
export const claveDeFila = (fila: Fila): string => {
  const elegidas = Array.isArray(fila.combinacion) ? (fila.combinacion as Fila[]) : []
  return claveDeVariante(Object.fromEntries(elegidas.map((c) => [texto(c.opcion), texto(c.valor)])))
}

/** «Talla M · Color Rojo»: el título de una variante, no «Variante 7». */
export const tituloDeVariante = (fila: Fila): string =>
  (Array.isArray(fila.combinacion) ? (fila.combinacion as Fila[]) : [])
    .map((c) => [texto(c.opcion), texto(c.valor)].filter(Boolean).join(' '))
    .filter(Boolean)
    .join(' · ')

/**
 * Las variantes que faltan para tener todas las combinaciones de las opciones, listas para
 * añadir al final de las que ya hay. Las que existen no se tocan (tienen su precio y sus
 * existencias); las que sobran tampoco: quitar es cosa de quien sabe qué no vende.
 */
export const variantesQueFaltan = (opciones: OpcionDeProducto[], filas: Fila[]): { posibles: number; nuevas: Fila[] } => {
  const posibles = combinaciones(opciones)
  const existentes = new Set(filas.map(claveDeFila))
  const nuevas = posibles
    .filter((p) => !existentes.has(p.clave))
    .map((p) => ({ _clave: idDeFila(), combinacion: Object.entries(p.elegidas).map(([opcion, valor]) => ({ opcion, valor, _clave: idDeFila() })) }))
  return { posibles: posibles.length, nuevas }
}
